import React from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { snakeCase, mapKeys } from 'lodash';
import { camelCase } from 'lodash-es';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import { SIGN_UP } from '../../constants/routing';
import {
  AUTH_PARAM_KEYS,
  OTETSUTABI_AUTH_VALUE,
  AuthParamKeys,
} from '~/models/Auth/AuthParamModel';
import { LOCAL_STORAGE_PROVIDER_KEY } from '../../constants/localStorage';
import { ProviderType, PROVIDER_TYPE } from './constants';
import { MAIL_BASE } from '../../../shared/styles/styleConstants';
import { makeButtonStyles } from './ProviderButtons';
import AuthButtons from './AuthButtons';
import ProviderConfirmButton from './ProviderConfirmButton';

const LOGO_SIZE = 30;

const useStyles = makeStyles((theme) => ({
  header: {
    color: '#333333',
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  description: {
    color: '#333333',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  alert: {
    margin: theme.spacing(0, 'auto', 4),
    maxWidth: 400,
  },
  errorMessage: {
    margin: 0,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    '& > a': {
      margin: theme.spacing(0, 'auto', 2.5),
    },
  },
  mail: makeButtonStyles(theme.palette.common.white, MAIL_BASE, true, true),
  startIcon: {
    marginRight: LOGO_SIZE,
  },
  note: {
    fontSize: theme.typography.pxToRem(14),
    maxWidth: 300,
    margin: theme.spacing(1, 'auto', 0),
  },
  progress: {
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: 300,
    '& > *': {
      margin: 'auto',
    },
  },
  linkRoot: {
    color: '#0000ee',
    '&:hover': {
      color: '#0000ee',
    },
  },
}));

// PROVIDER_TYPEと同じkey名
const PROVIDER_NAME = {
  google_oauth2: 'Google',
  facebook: 'Facebook',
  line: 'LINE',
  one: '小田急ONE',
};

const MAINTENANCE_PROVIDERS: string[] =
  (process.env.MAINTENANCE_PROVIDER?.split(',')
    .map((p) => PROVIDER_NAME[p])
    .filter(Boolean)
    .join(', ') || []) as string[];

const ERROR_TYPE = {
  USER_NOT_FOUND: 'userNotFound',
};

type Props = {
  signup?: boolean;
  errorType?: string | null;
  errorMessage?: string | null;
  authParams?: { [key in AuthParamKeys]?: string | number };
};

const AuthContent = ({
  signup,
  errorType: errorTypeSnake,
  authParams = {},
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const errorType = camelCase(errorTypeSnake);
  const lastLoginProvider: ProviderType | '' =
    localStorage.getItem(LOCAL_STORAGE_PROVIDER_KEY) || '';

  const isMaintenance = MAINTENANCE_PROVIDERS?.length > 0;
  const isValidProvider =
    Object.values(PROVIDER_TYPE).includes(lastLoginProvider);

  const mergedAuthParams = Object.assign(
    // クエリパラメータの値を取得して、新規登録画面の時だけ展開する
    signup ? { ...queryString.parse(window.location.search) } : {},
    authParams,
    {
      [AUTH_PARAM_KEYS.OTETSUTABI_AUTH]: signup
        ? OTETSUTABI_AUTH_VALUE.SIGNUP
        : OTETSUTABI_AUTH_VALUE.SIGN_IN,
    },
  );
  const authParamStr = queryString.stringify(
    mapKeys(authParams, (v, k) => snakeCase(k)),
  );

  return (
    <>
      <Typography className={classes.header} align="center">
        {t(signup ? 'logins.signupViaSns' : 'logins.loginViaSns')}
      </Typography>
      <Typography className={classes.description} align="center">
        <span>{t('logins.description1')}</span>
        <br />
        <span>{t('logins.description2')}</span>
      </Typography>
      {isMaintenance && (
        <Alert severity="error" icon={false} className={classes.alert}>
          {t('logins.maintenance', {
            provider: MAINTENANCE_PROVIDERS,
          })}
        </Alert>
      )}
      {errorType === ERROR_TYPE.USER_NOT_FOUND && (
        <Alert severity="error" icon={false} className={classes.alert}>
          <p className={classes.errorMessage}>{t('logins.userNotFound')}</p>
          <Link
            href={`${SIGN_UP}?${authParamStr}`}
            rel="noreferrer"
            underline="always"
            color="initial"
            classes={{ root: classes.linkRoot }}
          >
            {t('logins.signupHere')}
          </Link>
        </Alert>
      )}
      <div className={classes.wrapper}>
        <AuthButtons
          signup={signup}
          authParams={mergedAuthParams}
          lastLoginProvider={isValidProvider ? lastLoginProvider : undefined}
        />
        {/*{signup && (*/}
        {/*  <ProviderButton*/}
        {/*    provider={t('logins.mail')}*/}
        {/*    signup={signup} className={classes.mail}*/}
        {/*    startIcon={<MailIcon className={classes.mail} />}*/}
        {/*    href={USERS_SIGNUP_CREATEACCOUNT}*/}
        {/*  />*/}
        {/*)}*/}
        {signup ? (
          <Typography color="textSecondary" className={classes.note}>
            {t('logins.signupNote')}
          </Typography>
        ) : (
          <ProviderConfirmButton />
        )}
      </div>
      {/*{!signup && <EmailLoginForm/>}*/}
    </>
  );
};

export default AuthContent;
